import { PropsWithChildren, useCallback, useMemo } from 'react';

import { AuthenticationContext } from '../contexts/authentication-context';
import { useTokenContext } from '../hooks/use-token-context';
import { useUser } from '../hooks/use-user';

interface AuthenticationContextProviderProps extends PropsWithChildren {
   profileUrl?: string;
   loginUrl?: string;
   logoutUrl?: string;
   canAuthenticate?: boolean;
}

export const AuthenticationContextProvider = ({
   loginUrl = '/api/auth/login',
   logoutUrl = '/api/auth/logout',
   canAuthenticate = true,
   children
}: AuthenticationContextProviderProps) => {
   const { user, isLoading: isLoadingUser, invalidate } = useUser();
   const { accessToken, isLoading: isLoadingToken, refreshToken: refreshTokenFn } = useTokenContext();

   const refreshToken = useCallback(async () => {
      await refreshTokenFn();
      await invalidate();
   }, [refreshTokenFn, invalidate]);

   const login = useCallback(
      (redirectUrl?: string, language?: string) => {
         const searchParams = new URLSearchParams();

         if (language) {
            searchParams.set('language', language);
         } else {
            const languageMatch = window.location.pathname.match(/\/([a-z]{2})(?:-[A-Z]{2})/);
            if (languageMatch) {
               searchParams.set('language', languageMatch[1].toLowerCase());
            }
         }

         if (redirectUrl) {
            searchParams.set('returnTo', redirectUrl);
         }

         const url = new URL(`${loginUrl}?${searchParams.toString()}`, window.location.origin);
         window.location.href = url.toString();
      },
      [loginUrl]
   );

   const logout = useCallback(() => {
      const url = new URL(logoutUrl, window.location.origin);
      window.location.href = url.toString();
   }, [logoutUrl]);

   const value = useMemo(
      () => ({
         user,
         accessToken,
         loading: isLoadingUser || isLoadingToken,
         refreshToken,
         login,
         logout,
         canAuthenticate
      }),
      [user, accessToken, isLoadingUser, isLoadingToken, refreshToken, login, logout, canAuthenticate]
   );

   return <AuthenticationContext value={value}>{children}</AuthenticationContext>;
};
